import axios, {AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, HttpStatusCode} from 'axios';
// Track login modal state and pending retries
import {showErrorNotification, showRetryNotification, showSuccessNotification} from "../notifications";
import {IStandardErrorResponse} from "../types/IStandardErrorResponse";
import {openLoginModal} from "../../components/pages/LoginPage";
import {isRedirectAuthMode, markApiResponse, reportAuthExpired, trackResponselessFailure} from "./reauthRedirect";
import {modals} from "@mantine/modals";
import {v4 as uuidv4} from "uuid";

// Extend AxiosRequestConfig to include retry properties
export interface RetryConfig extends AxiosRequestConfig {
    retry?: boolean;
    maxRetries?: number;
    backoff?: number;
    retryCount?: number;
    retryCheck?: RetryCheckCallback
    notificationId?: string
    // allows you to mutate the retry config or perform some other operation
    // before the retry wait
    onRetry?: OnRetryCallback
    isAfterLoginRetry?: boolean;
    silent?: boolean; // if true, suppresses notifications
}

/** Define Callback used to determine if we should retry or not */
export type RetryCheckCallback = ({status, responseError, config}: {
    status?: HttpStatusCode,
    responseError?: IStandardErrorResponse,
    config?: RetryConfig
}) => boolean

/**
 * Callback to perform an action or update the config when retrying calls
 *
 */
export type OnRetryCallback = (backoff: number, config: RetryConfig) => Promise<RetryConfig>

export const DefaultRetryCheck: RetryCheckCallback = ({status, config}) => {
    // no status code usually means server down so retry
    if (status === undefined) {
        return true
    }

    // retry on auth errors if it's after login retry
    if (config?.isAfterLoginRetry && (status === HttpStatusCode.Unauthorized || status === HttpStatusCode.Forbidden)) {
        return true
    }

    // default retry when we got a response is only on timeouts
    switch (status) {
        case HttpStatusCode.RequestTimeout:
        case HttpStatusCode.GatewayTimeout:
            return true
        default:
            return false
    }
}


/**
 * Default timeout (ms) for API calls. Makes offline requests fail promptly
 * instead of hanging until the OS/webview network stack gives up — the
 * offline indicator, the write drainer and the presigned-URL step of
 * browser uploads all depend on requests rejecting on schedule.
 *
 * Long-running file uploads (the S3 PUT) override this per-request with
 * UPLOAD_TIMEOUT_MS — 1+ GB files legitimately take a long time.
 */
export const DEFAULT_API_TIMEOUT_MS = 10_000

/** Per-request timeout for file-content uploads: 2 hours. */
export const UPLOAD_TIMEOUT_MS = 2 * 60 * 60 * 1000

// Create an Axios instance
export const apiClient: AxiosInstance = axios.create({
    baseURL: `${window.BIOS.neurotune_base_url}/api/`, // Replace with your API base URL
    responseType: "json",
    headers: {...window.BIOS.headers},
    withCredentials: true, // Include cookies in requests (if needed for sessions)
    maxRedirects: 0,
    timeout: DEFAULT_API_TIMEOUT_MS,
});

let isLoginModalOpen = false;
const pendingAuthRetries: (() => void)[] = [];

// Add a response interceptor
apiClient.interceptors.response.use(
    (response: AxiosResponse) => {
        // Any real response is evidence that auth transport is alive —
        // resets the response-less failure watchdog (see reauthRedirect).
        markApiResponse();
        // Return successful responses unchanged
        return response;
    },
    async (error: AxiosError) => {
        let title = "Network Error"
        let message = "Please check your network connection."
        const config = error.config as RetryConfig
        const showAndReject = (notificationId?: string) => {
            if (!config?.silent) {
                showErrorNotification({id: notificationId, title: title, message: message})
            }
            return Promise.reject(error)
        }
        const showSuccess = (notificationId?: string) => {
            // only show success if we have retried at least once
            if (notificationId && !config.silent)
                showSuccessNotification({id: notificationId, title: "Retry Successful", message: ""})
        }

        // our retry function
        const attemptRetry = async () => {
            if (!config || !config.retry) {
                return showAndReject(config.notificationId)
            }

            config.retryCount = config.retryCount ?? 1;

            // exceeded retry count so show error and reject
            if (config.retryCount > (config.maxRetries ?? 3)) {
                return showAndReject(config.notificationId)
            }

            // setup retry and resend
            config.retryCount += 1;

            // calculate exponential backoff with a maximum time of 15 seconds
            const backoff = Math.min(15000, (config.backoff ?? 300) * Math.pow(2, config.retryCount));

            let newConfig: RetryConfig = {
                ...config,
                ...(config.onRetry ? await config.onRetry(backoff, config) : {}),
                retryCount: config.retryCount,
            }

            await new Promise(resolve => setTimeout(resolve, backoff))

            // retry the request
            return apiClient(newConfig).then((response) => {
                showSuccess(newConfig.notificationId)
                return response;
            }).catch(error => {
                return Promise.reject(error)
            })
        }

        //  if we got a response handle it differently than if the request itself failed
        // IE server is down in this case
        if (error.response) {
            markApiResponse();
            const {status, data} = error.response;

            // Check for 401 Unauthorized or 403 Forbidden and redirect for login
            switch (status) {
                case HttpStatusCode.Forbidden:
                    // forbidden means you cannot perform that operation even after login
                    // so fail and let user know because it's not going to work
                    return showAndReject(config.notificationId)
                case HttpStatusCode.Unauthorized:
                    // automatic retries not allowed for auth errors in redirect mode because the
                    // redirect will lose the original request context during the redirect.
                    // isRedirectAuthMode() is Tauri-aware: inside the NDC webview the
                    // redirect path is the ONLY recovery, so it applies regardless of the
                    // resolved loginMode.
                    if (isRedirectAuthMode()) {
                        // Set the store flag LoadingPage renders the LoginPage
                        // from — in place, so the deep link survives.
                        reportAuthExpired(`401 from ${config?.url ?? 'unknown request'}`);
                        return Promise.reject(error);
                    }
                    if (config.isAfterLoginRetry) {
                        // After login, treat auth errors as normal errors and retry if applicable
                        const responseError: IStandardErrorResponse | undefined = data ? data as IStandardErrorResponse : undefined;
                        const retryCheck = config.retryCheck ?? DefaultRetryCheck
                        if (retryCheck({status, responseError, config})) {
                            return await attemptRetry()
                        } else {
                            return showAndReject(config.notificationId)
                        }
                    } else {
                        // Here we want to :
                        // 1. Open the login modal to allow the user to relogin
                        // 2. Queue up all retries while the login modal is open
                        // 3. After successful login, retry all queued requests
                        // This allows for seamless recovery from expired sessions as
                        // well as preventing multiple login modals from opening
                        return await new Promise((resolve, reject) => {
                            const retryAfterLogin = async () => {
                                const retryCheck = config.retryCheck ?? DefaultRetryCheck;
                                if (retryCheck({config})) {
                                    config.isAfterLoginRetry = true;
                                    try {
                                        const response = await attemptRetry();
                                        resolve(response);
                                    } catch (error) {
                                        reject(error);
                                    }
                                } else {
                                    reject(error);
                                }
                            };

                            if (isLoginModalOpen) {
                                pendingAuthRetries.push(retryAfterLogin);
                            } else {
                                isLoginModalOpen = true;
                                const modalId = uuidv4();
                                openLoginModal({
                                    modalId,
                                    size: "md",
                                    onSuccess: async () => {
                                        modals.close(modalId);
                                        await retryAfterLogin();
                                        isLoginModalOpen = false;
                                        while (pendingAuthRetries.length > 0) {
                                            const nextRetry = pendingAuthRetries.shift();
                                            if (nextRetry) await nextRetry();
                                        }
                                    },
                                });
                            }
                        });
                    }
                default:
                    const responseError: IStandardErrorResponse | undefined = data ? data as IStandardErrorResponse : undefined;

                    const retryCheck = config.retryCheck ?? DefaultRetryCheck
                    if (retryCheck({status, responseError, config})) {
                        return await attemptRetry()
                    } else {
                        return showAndReject(config.notificationId)
                    }
            }
        } else {
            // No response at all: network down, timeout, or — in the NDC
            // webview — requests that hang after the auth cookie is deleted
            // and only die at the axios timeout. Track it: persistent
            // response-less failures while the server is reachable mean
            // re-auth is needed (see reauthRedirect.trackResponselessFailure).
            trackResponselessFailure();
            const retryCheck = config.retryCheck ?? DefaultRetryCheck
            if (config.retry  &&  retryCheck({config})) {
                return await attemptRetry()
            } else {
                return showAndReject(config.notificationId)
            }
        }
    }
);

export const createRetryingNotificationHandler= ({message, withRetryTime = true}: {
    message: string,
    withRetryTime?: boolean
}) : OnRetryCallback => {
    return (backoff: number, config: RetryConfig) => {
        if (config?.retryCount && config?.retryCount > 3 && !config.silent) {
            const notificationId = showRetryNotification({
                id: config.notificationId,
                message: message + (withRetryTime ? ` Retrying in ${Math.round(backoff / 1000)} seconds` : "")
            })
            return Promise.resolve({...config, notificationId: config.notificationId ?? notificationId})
        }
        return Promise.resolve(config)
    }
}